import { useEffect, useRef, useState } from 'react'
import { Dialog } from '@mui/material'
import styled from 'styled-components'
import { SkeletonRows } from 'components/Skeleton'
import taskLinksAPI from 'features/taskLinks/taskLinksAPI'
import { taskStatusMeta } from 'features/taskManagement/taskStatusConstants'
import { formatDay } from 'features/actions/workspace/remedialDates'
import { RELATION_COPY } from 'features/taskLinks/linkCopy'
import { ACCENT, ACCENT_SOFT, BORDER, INK, LINK, MUTED, SecondaryButton } from 'features/actions/workspace/ui'

const RELATIONS = ['handles', 'related']

const LinkTaskDialog = ({ open, bpId, remedialTitle, excludeIds = [], onClose, onPick, zIndex = 100010 }) => {
  const [q, setQ] = useState('')
  const [rows, setRows] = useState(null)
  const [relation, setRelation] = useState('handles')
  const searchRef = useRef(null)

  useEffect(() => {
    if (!open) return
    setQ('')
    setRelation('handles')
  }, [open])

  useEffect(() => {
    if (!open || !bpId) return undefined
    let cancelled = false
    setRows(null)
    const timer = setTimeout(() => {
      taskLinksAPI.searchTasks(bpId, q)
        .then(res => { if (!cancelled) setRows(res?.data?.data || []) })
        .catch(() => { if (!cancelled) setRows([]) })
    }, 250)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [open, bpId, q])

  const visible = (rows || []).filter(task => !excludeIds.includes(task.id))

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
      aria-labelledby='link-task-title'
      sx={{ zIndex }}
      TransitionProps={{ onEntered: () => searchRef.current?.focus() }}
      PaperProps={{ sx: { borderRadius: '12px', m: 2, width: 'calc(100% - 32px)', maxHeight: 'calc(100% - 32px)' } }}
    >
      <Card>
        <Title id='link-task-title'>Link an existing task</Title>
        {remedialTitle && <Sub>Remedial: {remedialTitle}</Sub>}
        <Help>Choose how the task relates to the remedial, then pick the task from the list.</Help>
        <Choices role='radiogroup' aria-label='How the task relates to the remedial'>
          {RELATIONS.map(key => (
            <Choice
              key={key}
              type='button'
              role='radio'
              aria-checked={relation === key}
              $on={relation === key}
              onClick={() => setRelation(key)}
            >
              <ChoiceLabel>{RELATION_COPY[key].label}</ChoiceLabel>
              <ChoiceHelp>{RELATION_COPY[key].help}</ChoiceHelp>
            </Choice>
          ))}
        </Choices>
        <Search
          ref={searchRef}
          type='search'
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder='Search tasks by name'
          aria-label='Search tasks by name'
        />
        <List aria-live='polite'>
          {rows === null && <SkeletonRows rows={4} gap={10} widths={['100%']} />}
          {rows !== null && visible.length === 0 && (
            <Empty>{q ? 'No tasks match that name. Try a different word.' : 'There are no tasks on this building yet.'}</Empty>
          )}
          {rows !== null && visible.map(task => {
            const status = taskStatusMeta(task.status)
            const blocked = relation === 'handles' && !!task.isRoutine
            return (
              <Pick key={task.id} type='button' disabled={blocked} onClick={() => onPick(task, relation)}>
                <PickTitle>{task.title || 'Untitled task'}</PickTitle>
                <PickMeta>
                  {task.deadline && <span>Due {formatDay(task.deadline)}</span>}
                  {task.isRoutine && <span>Routine</span>}
                  <Status style={{ color: status.color, background: status.bg }}>{status.label}</Status>
                </PickMeta>
                {blocked && <PickReason>This is a routine compliance task, so it can only be linked as related.</PickReason>}
              </Pick>
            )
          })}
        </List>
        <Footer>
          <SecondaryButton type='button' onClick={onClose}>Cancel</SecondaryButton>
        </Footer>
      </Card>
    </Dialog>
  )
}

export default LinkTaskDialog

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 auto;
  min-height: 0;
  padding: 20px;
  overflow: hidden;
  @media (max-width: 768px) { padding: 16px; }
`

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: ${INK};
`

const Sub = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  overflow-wrap: anywhere;
`

const Help = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: ${MUTED};
`

const Choices = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`

const Choice = styled.button`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-height: 44px;
  padding: 10px 12px;
  text-align: left;
  font-family: inherit;
  border-radius: 8px;
  cursor: pointer;
  background: ${({ $on }) => ($on ? ACCENT_SOFT : '#fff')};
  border: 2px solid ${({ $on }) => ($on ? ACCENT : BORDER)};
  &:focus-visible { outline: 2px solid ${LINK}; outline-offset: 2px; }
`

const ChoiceLabel = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${INK};
`

const ChoiceHelp = styled.span`
  font-size: 14px;
  line-height: 1.4;
  color: ${MUTED};
`

const Search = styled.input`
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid #d4d4d8;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  &:focus { outline: 2px solid ${ACCENT}; outline-offset: 1px; }
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 auto;
  min-height: 120px;
  overflow-y: auto;
`

const Empty = styled.div`
  padding: 8px 2px;
  font-size: 15px;
  color: ${MUTED};
`

const Pick = styled.button`
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 10px;
  min-height: 48px;
  padding: 8px 12px;
  text-align: left;
  font-family: inherit;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  &:hover:not(:disabled) { border-color: ${ACCENT}; background: ${ACCENT_SOFT}; }
  &:focus-visible { outline: 2px solid ${LINK}; outline-offset: 2px; }
  &:disabled { cursor: not-allowed; background: #f9fafb; }
`

const PickReason = styled.span`
  flex-basis: 100%;
  font-size: 14px;
  color: ${MUTED};
`

const PickTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  overflow-wrap: anywhere;
`

const PickMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  font-size: 13px;
  color: ${MUTED};
`

const Status = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`
